import React from 'react'
import cx from 'classnames'
import { array } from 'helpers'
import { useDevice } from 'device'

import type { Label } from 'typings/graphql'

import { ScrollableContainer } from 'components/layout'
import { Carousel } from 'components/dataDisplay'

import ProductCardSmall, { type ProductCardSmallProps } from 'compositions/productCards/ProductCardSmall/ProductCardSmall'
import ProductCardSmallSkeleton from 'compositions/productCards/ProductCardSmallSkeleton/ProductCardSmallSkeleton'

import ProductCardDetailed, { type ProductCardDetailedProps } from 'compositions/productCards/ProductCardDetailed/ProductCardDetailed'
import ProductCardDetailedSkeleton from 'compositions/productCards/ProductCardDetailedSkeleton/ProductCardDetailedSkeleton'

import s from './ProductCarousel.scss'


type Products = {
  desktopType?: 'small'
  products: ProductCardSmallProps['data'][]
} | {
  desktopType: 'detailed'
  products: ProductCardDetailedProps['data'][]
}

export type ProductCarouselProps = {
  metadata: QueueModule.AddItemInput['metadata']
  excludeLabels?: Label[]
  extraNode?: React.ReactNode
  isEcommerce?: boolean
  isFetching?: boolean
  withoutNotes?: boolean
  withQueueLeadModal?: boolean
  lazy?: boolean
} & Products

const ProductCarousel: React.FunctionComponent<ProductCarouselProps> = (props) => {
  const { desktopType = 'small', products, metadata, excludeLabels, extraNode,
    isEcommerce, isFetching, withoutNotes, withQueueLeadModal, lazy } = props
  const { isMobile } = useDevice()

  const isTypeSmall = desktopType === 'small'

  const [ SkeletonComponent, ProductComponent, visibleNodeAmount ] = isTypeSmall || isMobile
    ? [ ProductCardSmallSkeleton, ProductCardSmall, 6 ]
    : [ ProductCardDetailedSkeleton, ProductCardDetailed, 3 ]

  let itemsNode

  if (isFetching) {
    itemsNode = array.range(1, visibleNodeAmount).map((index) => (
      <SkeletonComponent key={index} />
    ))
  }
  else if (!products?.length) {
    return null
  }
  else {
    itemsNode = products.map((data) => (
      <ProductComponent
        className="h-full"
        key={data.id}
        data={data}
        excludeLabels={excludeLabels}
        isEcommerce={isEcommerce}
        withoutNotes={withoutNotes}
        buttonProps={{
          metadata,
          withQueueLeadModal,
        }}
        lazy={lazy}
      />
    ))
  }

  if (isMobile) {
    return (
      <ScrollableContainer className={cx(s.container, 'flex')}>
        <div className={cx(s.gridSmall, 'inline-grid grid-flow-col gap-16 py-16')}>
          {itemsNode}
        </div>
        {extraNode}
      </ScrollableContainer>
    )
  }

  return (
    <>
      {
        itemsNode.length > visibleNodeAmount ? (
          <Carousel className={s.carousel} withArrows slidesToScroll={visibleNodeAmount} slidesToShow={visibleNodeAmount}>
            {
              itemsNode.map((child, index) => (
                <div key={child.key || index} className="h-full px-8">
                  {child}
                </div>
              ))
            }
          </Carousel>
        ) : (
          <>
            <div className={cx(isTypeSmall ? s.gridSmall : s.gridDetailed, 'grid grid-flow-col justify-center gap-16')}>
              {itemsNode}
            </div>
          </>
        )
      }
    </>
  )
}


export default React.memo(ProductCarousel)
